import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import LoginPage from './features/auth/LoginPage';
import ForgotPasswordPage from './features/auth/ForgotPasswordPage';
import Dashboard from './features/dashboard/Dashboard';
import ProgramManager from './features/programs/ProgramManager';
import ProgramArchitect from './features/programs/ProgramArchitect';
import ProgramOverview from './features/programs/ProgramOverview';
import GymManager from './features/gym/GymManager';
import GymDetails from './features/gym/GymDetails';
import TrainersPage from './features/users/TrainersPage';
import TrainerDetailPage from './features/users/TrainerDetailPage';
import MembersPage from './features/users/MembersPage';
import MemberDetailPage from './features/users/MemberDetailPage';
import PaymentSuccessPage from './features/billing/PaymentSuccessPage';
import PaymentCancelPage from './features/billing/PaymentCancelPage';
import BillingPage from './features/billing/BillingPage';
import SubscriptionPlansPage from './features/subscription/pages/SubscriptionPlansPage';
import MainLayout from './components/layout/MainLayout';
import ComingSoon from './components/ComingSoon';
import { Toast, setToastCallback } from './components/Toast';
import { ConfirmDialog, setConfirmCallback } from './components/ConfirmDialog';
import { ProtectedRoute } from './components/ProtectedRoute';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3,
      staleTime: 30000, // 30 seconds
    },
  },
});

interface ToastData {
  id: number;
  message: string;
  type: 'success' | 'error' | 'info';
  description?: string;
}

interface ConfirmData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel?: () => void;
}

export default function App() {
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmData | null>(null);

  useEffect(() => {
    // Initialize toast callback
    setToastCallback((message, type, description) => {
      const id = Date.now();
      setToasts((prev) => [...prev, { id, message, type, description }]);
    });

    // Initialize confirm callback
    setConfirmCallback((options) => {
      setConfirmDialog(options);
    });
  }, []);

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Default route redirects to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          
          {/* Login page route */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Forgot password route */}
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          
          {/* Dashboard route (accessible after login) */}
          <Route path="/dashboard" element={<ProtectedRoute><MainLayout><Dashboard /></MainLayout></ProtectedRoute>} />
          
          {/* Programs route */}
          <Route path="/programs" element={<ProtectedRoute><MainLayout><ProgramManager /></MainLayout></ProtectedRoute>} />
          
          {/* Create/Edit Program routes - must come before :id route */}
          <Route path="/programs/create" element={<ProtectedRoute><MainLayout><ProgramArchitect /></MainLayout></ProtectedRoute>} />
          <Route path="/programs/:id/edit" element={<ProtectedRoute><MainLayout><ProgramArchitect /></MainLayout></ProtectedRoute>} />
          
          {/* Program Overview route - must come after specific routes */}
          <Route path="/programs/:id" element={<ProtectedRoute><MainLayout><ProgramOverview /></MainLayout></ProtectedRoute>} />
          
          {/* Gym Management routes */}
          <Route path="/gyms" element={<ProtectedRoute><MainLayout><GymManager /></MainLayout></ProtectedRoute>} />
          <Route path="/gyms/:gymId" element={<ProtectedRoute><MainLayout><GymDetails /></MainLayout></ProtectedRoute>} />
          
          {/* User Management routes */}
          <Route path="/trainers" element={<ProtectedRoute><MainLayout><TrainersPage /></MainLayout></ProtectedRoute>} />
          <Route path="/trainers/:trainerId" element={<ProtectedRoute><MainLayout><TrainerDetailPage /></MainLayout></ProtectedRoute>} />
          <Route path="/members" element={<ProtectedRoute><MainLayout><MembersPage /></MainLayout></ProtectedRoute>} />
          <Route path="/members/:memberId" element={<ProtectedRoute><MainLayout><MemberDetailPage /></MainLayout></ProtectedRoute>} />
          
          {/* Payment routes (no MainLayout needed) */}
          <Route path="/subscription/success" element={<PaymentSuccessPage />} />
          <Route path="/subscription/cancel" element={<PaymentCancelPage />} />
          
          {/* Coming Soon routes for unimplemented features */}
          <Route path="/verifications" element={<ProtectedRoute><MainLayout><ComingSoon feature="Verifications" /></MainLayout></ProtectedRoute>} />
          <Route path="/billing" element={<ProtectedRoute><MainLayout><BillingPage /></MainLayout></ProtectedRoute>} />
          <Route path="/subscription/plans" element={<ProtectedRoute><MainLayout><SubscriptionPlansPage /></MainLayout></ProtectedRoute>} />
          <Route path="/subscription" element={<ProtectedRoute><MainLayout><ComingSoon feature="Subscription" /></MainLayout></ProtectedRoute>} />
          
          {/* Catch-all route redirects to login */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>

        {/* Toast Container */}
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {toasts.map((toast) => (
            <Toast
              key={toast.id}
              type={toast.type}
              title={toast.message}
              description={toast.description}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>

        {/* Confirm Dialog */}
        {confirmDialog && (
          <ConfirmDialog
            isOpen={true}
            title={confirmDialog.title}
            message={confirmDialog.message}
            confirmText={confirmDialog.confirmText}
            cancelText={confirmDialog.cancelText}
            type={confirmDialog.type}
            onConfirm={confirmDialog.onConfirm}
            onCancel={() => {
              confirmDialog.onCancel?.();
              setConfirmDialog(null);
            }}
          />
        )}
      </BrowserRouter>
    </QueryClientProvider>
  );
}
